import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Component, OnInit, Input, EventEmitter, Output, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertService } from '@app/_services';
import * as moment from 'moment';
import { PrimeNGConfig } from 'primeng/api';
import { CompositeListingService } from '../composite-listing.service';
import { CompositeListing } from '../composite-listing';
import { PropertyService } from '@app/_components/property/property.service';

@Component({
    selector: 'app-composite-listing',
    templateUrl: './composite-listing.component.html',
    styleUrls: ['./composite-listing.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class CompositeListingComponent implements OnInit {
	properties: any;
	compositeShortTermRentalId: any;
	sitePriorities: { name: string; value: number; }[];
	// Listing-type rules (parent → allowed children):
	//   - Composite parent (listingType = 2) → standard STR only (listingType = 1)
	//   - Hotel Room parent (listingType = 3) → standard STR (1) OR Composite (2);
	//     nested Hotel Rooms (3) are rejected.
	// Both rules are enforced inline before the form submits and again server-side.
	parentListingType: number | null = null;

	constructor(
		private primengConfig: PrimeNGConfig,
		private compositeListingService: CompositeListingService,
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private propertyService: PropertyService,
		private formBuilder: UntypedFormBuilder,
		@Inject(MAT_DIALOG_DATA) data: any) {
		this.formTitle = data.title,
			this.compositeListing = data.compositeListing,
			this.compositeShortTermRentalId = data.compositeShortTermRentalId,
			this.parentListingType = data.parentListingType ?? null,
			this.statuses = compositeListingService.statuses;
			this.sitePriorities = compositeListingService.sitePriorities;
	}

	@Input() compositeListing: CompositeListing;
	@Output() doAdd = new EventEmitter<any>();
	@Output() doUpdate = new EventEmitter<any>();
	@Output() doDelete = new EventEmitter<any>();

	// data
	compositeListingId: number;

	// forms
	form1: UntypedFormGroup;
	formLoading = false;
	formAlert: String;
	formTitle: String;

	statuses: any;

	validationMessages = {
		'compositeListingId': [],
		'compositeShortTermRentalId': [
			{ type: 'required', message: 'Required' }],
		'shortTermRentalId': [
			{ type: 'required', message: 'Required' }
		],
		listingPriority:[]
	};

	ngOnInit() {
		this.fetchProperties();
		this.primengConfig.ripple = true;
		this.formBuild();

		// if we were passed the whole compositeListing object
		if (this.compositeListing) {
			this.compositeListingId = this.compositeListing.compositeListingId;
			this.fixDates();
			this.form1.patchValue(this.compositeListing);
		}
	}

	fetchProperties() {
		if (!this.properties || this.properties === undefined || this.properties.length < 1) {
			try {
				this.propertyService.propertySummaryForShortData()
					.subscribe(
						res => {
							// console.log('findAllPropertiesSummary: ' + JSON.stringify(res));
							this.properties = res.data.properties;
							if (this.compositeShortTermRentalId) {
								this.form1.patchValue({
									compositeShortTermRentalId: Number(this.compositeShortTermRentalId)
								});
							}
							return this.properties;
						},
						err => {
							console.log('Feature-Update FAIL: ' + JSON.stringify(err));
							this.alertService.error(err);
						},
						() => { });
			} catch (error) {
				console.log('Form Invalid: ' + JSON.stringify(error));
				this.alertService.error(error);
			}
		}
		else {
			// console.log('properties are ' + this.properties);
			return (this.properties);
		}
	}

	onFormDelete() {
		console.log('onRowDelete: ' + JSON.stringify(event));
		this.confirmationService.confirm({
			message: 'Are you sure you want to delete Id# ' + this.compositeListingId + '?',
			header: 'Confirm',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.formDelete()
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
			}
		});
	}

	/**
	 * Child dropdown options — narrow what the user can pick based on the parent's listingType:
	 *   - Composite parent (2): standard STR only (1).
	 *   - Hotel Room parent (3): standard STR (1) OR Composite (2). Nested Hotel Rooms blocked.
	 *   - Anything else: full property list.
	 */
	get childPropertyOptions(): any[] {
		if (!this.properties) return [];
		if (this.parentListingType === 2) {
			// Composite parent → standard STR only
			return this.properties.filter((p: any) => p.listingType == null || p.listingType === 1);
		}
		if (this.parentListingType === 3) {
			// Hotel Room parent → STR or Composite (no nested Hotel Rooms)
			return this.properties.filter((p: any) => p.listingType == null || p.listingType === 1 || p.listingType === 2);
		}
		return this.properties;
	}

	/**
	 * Virtual-listing nesting rule:
	 *   - Composite parent (2) → child must be standard STR (listingType = 1).
	 *   - Hotel Room parent (3) → child must be standard STR (1) or Composite (2);
	 *     a Hotel Room child (3) is rejected — no nested Hotel Rooms.
	 */
	private validateChildListingType(): boolean {
		// Only virtual parents (2 or 3) enforce the rule; a missing parent type passes through.
		if (this.parentListingType !== 2 && this.parentListingType !== 3) return true;
		const childId = this.form1.get('shortTermRentalId')?.value;
		if (!childId || !this.properties) return true;
		const child = this.properties.find((p: any) => p.shortTermRentalId == childId);
		if (!child) return true;
		if (child.listingType == null) return true;

		const isCompositeParent  = this.parentListingType === 2;
		const isHotelRoomParent  = this.parentListingType === 3;
		const isCompositeChild   = child.listingType === 2;
		const isHotelRoomChild   = child.listingType === 3;

		// Hotel Room children are never allowed under any virtual parent.
		// Composite children are blocked only under a Composite parent.
		const invalid =
			isHotelRoomChild ||
			(isCompositeParent && isCompositeChild);

		if (invalid) {
			const parentLabel = isHotelRoomParent ? 'Hotel Room' : 'Composite';
			const childLabel  = isCompositeChild ? 'Composite' : isHotelRoomChild ? 'Hotel Room' : ('listingType=' + child.listingType);
			const allowed = isHotelRoomParent ? 'standard STR or Composite' : 'standard STR';
			this.messageService.add({
				severity: 'error',
				summary: 'Invalid Child Listing',
				detail: parentLabel + ' parents may only contain ' + allowed + ' children. Selected child is a ' + childLabel + ' — not allowed.',
				life: 6000
			});
			return false;
		}
		return true;
	}

	formAdd() {
		try {
			// stop here if form is invalid
			this.form1.markAllAsTouched();
			if (this.form1.invalid) {
				return;
			}
			if (!this.validateChildListingType()) {
				return;
			}
			console.log('property-add post: ' + JSON.stringify(this.form1.value));
			this.formLoading = true;
			this.compositeListingService.add(this.form1.value)
				.subscribe(
					(res: any) => {
						console.log('property-add SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.compositeListing = res.data.compositeListing;
						this.doAdd.emit(this.compositeListing);
					},
					(err: any) => {
						console.log('compositeListing-add FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}

	formUpdate() {
		console.log('compositeListing: ' + JSON.stringify(this.form1.value));
		try {
			// stop here if form is invalid
			this.form1.markAllAsTouched();
			if (this.form1.invalid) {
				return;
			}
			if (!this.validateChildListingType()) {
				return;
			}
			this.formLoading = true;

			this.compositeListingService.update(this.form1.value)
				.subscribe(
					(res: any) => {
						console.log('property-update SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.compositeListing = res.data.compositeListing;
						this.doUpdate.emit(this.compositeListing);
					},
					(err: any) => {
						console.log('property-update FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	formDelete() {
		try {
			this.compositeListingService.delete(this.compositeListing.compositeListingId)
				.subscribe(
					(res: any) => {
						console.log('compositeListing-delete SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.doDelete.emit(this.compositeListing);
					},
					(err: any) => {
						console.log('compositeListing-add FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			this.alertService.error(error);
		}
	}

	formBuild() {
		this.form1 = this.formBuilder.group({
			compositeListingId: [this.compositeListingId],
			compositeShortTermRentalId: ['', Validators.required],
			shortTermRentalId: ['', Validators.required],
			listingPriority:[]
		});
	}

	fixDates() {
		// create date objects - why can't primeng do this?
		this.compositeListing.compositeListingDate = moment(this.compositeListing.compositeListingDate).toDate();
	}
}
